import type { IMetricsColumns, IPageAnalyticMapping } from '../types/metrics';
import type { TPageAnalyticHeading } from '../types/page-analytic';
import type { IndexTableProps } from '@shopify/polaris';

import {
  getBounceRateMetric,
  getTotalSessionMetric,
  getAverageTimeOnPageMetric,
  getTotalPageViewMetric,
  getRevenuePerVisitorMetric,
  getAddToCartRateMetric,
  getAverageOrderValueMetric,
  getConversionRateMetric,
  getTotalRevenueMetric,
  getTotalOrderMetric,
  getTotalVisitorMetric,
  getPathAnalysisMetric,
  getTotalSessionByDeviceMetric,
  getNewReturningCustomerMetric,
  getTotalATCMetric,
  getTotalSessionReachedCheckoutMetric,
  getTotalSessionWithCartAdditionMetric,
  getTotalSessionCompletedCheckoutMetric,
  getLinkCtrMetric,
} from '~/modules/analytics';
import { IPAMetricKey } from '~/modules/analytics/types';

import {
  PA_SORT_METRIC_BR,
  PA_SORT_METRIC_CR,
  PA_SORT_METRIC_CTR,
  PA_SORT_METRIC_SESSIONS,
  PA_PAGE_TITLE_KEY,
} from './pageAnalytic';

export const METRICS_COLUMNS: IMetricsColumns = {
  [IPAMetricKey.PAGE_SESSIONS]: { label: 'Sessions' },
  [IPAMetricKey.PAGE_BR]: { label: 'Bounce rate' },
  [IPAMetricKey.PAGE_CTR]: { label: 'Click-through rate' },
  [IPAMetricKey.PAGE_CR]: { label: 'Conversion rate' },
  [IPAMetricKey.PAGE_VISITORS]: { label: 'Visitors' },
  [IPAMetricKey.PAGE_AVG_TIME]: { label: 'Avg time on page' },
  [IPAMetricKey.PAGE_VIEWS]: { label: 'Pageviews' },
  [IPAMetricKey.PAGE_REVENUE]: { label: 'Revenue' },
  [IPAMetricKey.PAGE_ADDED_TO_CART]: { label: 'Added to cart' },
  [IPAMetricKey.PAGE_REACHED_CHECKOUT]: {
    label: 'Sessions that reached checkout',
  },
  [IPAMetricKey.PAGE_COMPLETE_CHECKOUT]: {
    label: 'Sessions that completed checkout',
  },
  [IPAMetricKey.PAGE_RPV]: {
    label: 'Revenue per visitor',
  },
  [IPAMetricKey.PAGE_CART_ADDITION]: {
    label: 'Sessions with cart additions',
  },
  [IPAMetricKey.PAGE_ADD_TO_CART_RATE]: {
    label: 'Add to cart rate',
  },
  [IPAMetricKey.PAGE_ORDERS]: {
    label: 'Orders',
  },
  [IPAMetricKey.PAGE_AOV]: {
    label: 'Average order value',
  },
  [IPAMetricKey.PAGE_SESSIONS_BY_VISITOR]: {
    label: 'Visitor by type',
  },
  [IPAMetricKey.PAGE_SESSIONS_BY_TRAFFIC]: {
    label: 'Sessions by traffic source',
  },
  [IPAMetricKey.PAGE_PATH_ANALYSIS]: {
    label: 'Journey analysis',
  },
  [IPAMetricKey.PAGE_SESSIONS_BY_DEVICE]: {
    label: 'Sessions by device type',
  },
  [IPAMetricKey.PAGE_NEW_RETURNING_CUSTOMER]: {
    label: 'New vs returning visitors',
  },
} as const;

export const METRIC_ORDER: IPAMetricKey[] = [
  IPAMetricKey.PAGE_SESSIONS,
  IPAMetricKey.PAGE_BR,
  IPAMetricKey.PAGE_CTR,
  IPAMetricKey.PAGE_CR,
  IPAMetricKey.PAGE_VISITORS,
  IPAMetricKey.PAGE_AVG_TIME,
  IPAMetricKey.PAGE_VIEWS,
  IPAMetricKey.PAGE_REVENUE,
  IPAMetricKey.PAGE_ADDED_TO_CART,
  IPAMetricKey.PAGE_REACHED_CHECKOUT,
  IPAMetricKey.PAGE_COMPLETE_CHECKOUT,
  IPAMetricKey.PAGE_RPV,
  IPAMetricKey.PAGE_CART_ADDITION,
  IPAMetricKey.PAGE_ADD_TO_CART_RATE,
  IPAMetricKey.PAGE_ORDERS,
  IPAMetricKey.PAGE_AOV,
];

export const PAGE_ANALYTIC_HEADING_DEFAULT: IndexTableProps['headings'] = [
  { title: 'Page title', id: PA_PAGE_TITLE_KEY },
];

export const MODAL_PAGE_ANALYTIC_HEADING_DEFAULT: IndexTableProps['headings'] = [
  ...PAGE_ANALYTIC_HEADING_DEFAULT,
  { title: 'Link', id: 'link' },
];

export const SESSIONS_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_SESSIONS].label,
  id: IPAMetricKey.PAGE_SESSIONS,
  alignment: 'end',
};

export const BOUNCE_RATE_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_BR].label,
  id: IPAMetricKey.PAGE_BR,
  alignment: 'end',
};

export const CTR_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_CTR].label,
  id: IPAMetricKey.PAGE_CTR,
  alignment: 'end',
};

export const CONVERSION_RATE_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_CR].label,
  id: IPAMetricKey.PAGE_CR,
  alignment: 'end',
};

export const VISITORS_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_VISITORS].label,
  id: IPAMetricKey.PAGE_VISITORS,
  alignment: 'end',
};

export const AVG_TIME_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_AVG_TIME].label,
  id: IPAMetricKey.PAGE_AVG_TIME,
  alignment: 'end',
};

export const PAGEVIEWS_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_VIEWS].label,
  id: IPAMetricKey.PAGE_VIEWS,
  alignment: 'end',
};

export const REVENUE_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_REVENUE].label,
  id: IPAMetricKey.PAGE_REVENUE,
  alignment: 'end',
};

export const ADDED_TO_CART_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_ADDED_TO_CART].label,
  id: IPAMetricKey.PAGE_ADDED_TO_CART,
  alignment: 'end',
};

export const REACHED_CHECKOUT_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_REACHED_CHECKOUT].label,
  id: IPAMetricKey.PAGE_REACHED_CHECKOUT,
  alignment: 'end',
};

export const COMPLETED_CHECKOUT_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_COMPLETE_CHECKOUT].label,
  id: IPAMetricKey.PAGE_COMPLETE_CHECKOUT,
  alignment: 'end',
};

export const REVENUE_PER_VISITOR_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_RPV].label,
  id: IPAMetricKey.PAGE_RPV,
  alignment: 'end',
};

export const CART_ADDITIONS_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_CART_ADDITION].label,
  id: IPAMetricKey.PAGE_CART_ADDITION,
  alignment: 'end',
};

export const ADD_TO_CART_RATE_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_ADD_TO_CART_RATE].label,
  id: IPAMetricKey.PAGE_ADD_TO_CART_RATE,
  alignment: 'end',
};

export const ORDERS_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_ORDERS].label,
  id: IPAMetricKey.PAGE_ORDERS,
  alignment: 'end',
};

export const AVERAGE_ORDER_VALUE_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_AOV].label,
  id: IPAMetricKey.PAGE_AOV,
  alignment: 'end',
};

export const SESSIONS_BY_VISITOR_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_SESSIONS_BY_VISITOR].label,
  id: IPAMetricKey.PAGE_SESSIONS_BY_VISITOR,
  alignment: 'end',
};

export const SESSIONS_BY_TRAFFIC_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_SESSIONS_BY_TRAFFIC].label,
  id: IPAMetricKey.PAGE_SESSIONS_BY_TRAFFIC,
  alignment: 'end',
};

export const PATH_ANALYSIS_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_PATH_ANALYSIS].label,
  id: IPAMetricKey.PAGE_PATH_ANALYSIS,
  alignment: 'end',
};

export const SESSIONS_BY_DEVICE_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_SESSIONS_BY_DEVICE].label,
  id: IPAMetricKey.PAGE_SESSIONS_BY_DEVICE,
  alignment: 'end',
};

export const NEW_RETURNING_CUSTOMER_COLUMN_HEADING: TPageAnalyticHeading = {
  title: METRICS_COLUMNS[IPAMetricKey.PAGE_NEW_RETURNING_CUSTOMER].label,
  id: IPAMetricKey.PAGE_NEW_RETURNING_CUSTOMER,
  alignment: 'end',
};

export const PAGE_ANALYTIC_MAPPING: IPageAnalyticMapping = {
  [IPAMetricKey.PAGE_SESSIONS]: {
    getMetricValue: getTotalSessionMetric,
    heading: SESSIONS_COLUMN_HEADING,
    sortOptions: PA_SORT_METRIC_SESSIONS,
  },
  [IPAMetricKey.PAGE_BR]: {
    getMetricValue: getBounceRateMetric,
    heading: BOUNCE_RATE_COLUMN_HEADING,
    sortOptions: PA_SORT_METRIC_BR,
  },
  [IPAMetricKey.PAGE_CTR]: {
    getMetricValue: getLinkCtrMetric,
    heading: CTR_COLUMN_HEADING,
    sortOptions: PA_SORT_METRIC_CTR,
  },
  [IPAMetricKey.PAGE_CR]: {
    getMetricValue: getConversionRateMetric,
    heading: CONVERSION_RATE_COLUMN_HEADING,
    sortOptions: PA_SORT_METRIC_CR,
  },
  [IPAMetricKey.PAGE_VISITORS]: {
    getMetricValue: getTotalVisitorMetric,
    heading: VISITORS_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_AVG_TIME]: {
    getMetricValue: getAverageTimeOnPageMetric,
    heading: AVG_TIME_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_VIEWS]: {
    getMetricValue: getTotalPageViewMetric,
    heading: PAGEVIEWS_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_REVENUE]: {
    getMetricValue: getTotalRevenueMetric,
    heading: REVENUE_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_ADDED_TO_CART]: {
    getMetricValue: getTotalATCMetric,
    heading: ADDED_TO_CART_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_REACHED_CHECKOUT]: {
    getMetricValue: getTotalSessionReachedCheckoutMetric,
    heading: REACHED_CHECKOUT_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_COMPLETE_CHECKOUT]: {
    getMetricValue: getTotalSessionCompletedCheckoutMetric,
    heading: COMPLETED_CHECKOUT_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_RPV]: {
    getMetricValue: getRevenuePerVisitorMetric,
    heading: REVENUE_PER_VISITOR_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_CART_ADDITION]: {
    getMetricValue: getTotalSessionWithCartAdditionMetric,
    heading: CART_ADDITIONS_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_ADD_TO_CART_RATE]: {
    getMetricValue: getAddToCartRateMetric,
    heading: ADD_TO_CART_RATE_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_ORDERS]: {
    getMetricValue: getTotalOrderMetric,
    heading: ORDERS_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_AOV]: {
    getMetricValue: getAverageOrderValueMetric,
    heading: AVERAGE_ORDER_VALUE_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_SESSIONS_BY_VISITOR]: {
    getMetricValue: getTotalVisitorMetric,
    heading: SESSIONS_BY_VISITOR_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_SESSIONS_BY_TRAFFIC]: {
    getMetricValue: getTotalSessionMetric,
    heading: SESSIONS_BY_TRAFFIC_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_PATH_ANALYSIS]: {
    getMetricValue: getPathAnalysisMetric,
    heading: PATH_ANALYSIS_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_SESSIONS_BY_DEVICE]: {
    getMetricValue: getTotalSessionByDeviceMetric,
    heading: SESSIONS_BY_DEVICE_COLUMN_HEADING,
  },
  [IPAMetricKey.PAGE_NEW_RETURNING_CUSTOMER]: {
    getMetricValue: getNewReturningCustomerMetric,
    heading: NEW_RETURNING_CUSTOMER_COLUMN_HEADING,
  },
};
